
export interface RecentEntry {
  id: string;
  kind: 'ticket' | 'task' | 'wiki' | 'person' | 'command';
  label: string;
  badge?: string;
  hint?: string;
  to?: string;
  at: number;
}

const MAX_RECENT = 8;
const recentKey = (userId: string) => `pridex-palette-recent:${userId}`;
const usageKey = (userId: string) => `pridex-palette-usage:${userId}`;

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
  }
}

export function loadRecent(userId: string): RecentEntry[] {
  const list = read<RecentEntry[]>(recentKey(userId), []);
  return Array.isArray(list) ? list.filter((e) => e && typeof e.id === 'string') : [];
}

export function pushRecent(userId: string, entry: Omit<RecentEntry, 'at'>): RecentEntry[] {
  const next = [{ ...entry, at: Date.now() }, ...loadRecent(userId).filter((e) => e.id !== entry.id)].slice(
    0,
    MAX_RECENT,
  );
  write(recentKey(userId), next);
  return next;
}

export function removeRecent(userId: string, id: string): RecentEntry[] {
  const next = loadRecent(userId).filter((e) => e.id !== id);
  write(recentKey(userId), next);
  return next;
}

export function clearRecent(userId: string) {
  write(recentKey(userId), []);
  write(usageKey(userId), {});
}

type Usage = Record<string, { n: number; at: number }>;

export function loadUsage(userId: string): Usage {
  const usage = read<Usage>(usageKey(userId), {});
  return usage && typeof usage === 'object' ? usage : {};
}

export function bumpUsage(userId: string, id: string) {
  const usage = loadUsage(userId);
  const prev = usage[id];
  usage[id] = { n: (prev?.n ?? 0) + 1, at: Date.now() };
  write(usageKey(userId), usage);
}

export function usageBoost(usage: Usage, id: string, now = Date.now()): number {
  const u = usage[id];
  if (!u) return 0;
  const days = (now - u.at) / 86_400_000;
  const decay = days < 1 ? 1 : days < 7 ? 0.6 : days < 30 ? 0.3 : 0.1;
  return Math.min(12, Math.log2(1 + u.n) * 4 * decay);
}
